/**
 * QuestionLibraryManager Component
 * Manages global question library for survey types
 * Accessible by sys_admin or users with required_role for the survey type
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Library, Plus, Save, RefreshCw, AlertCircle, Car, Monitor,
    Search
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferenceContext';
import { ThemedButton, ThemedCard, ThemedInput, ThemedBadge, ThemedSelect } from '../themed';
import QuestionCard from './QuestionCard';
import axios from '../../axiosConfig';

/**
 * QuestionLibraryManager - Manage global survey questions
 *
 * Features:
 * - View/edit/delete global questions by survey type
 * - Add new global questions
 * - Reorder questions
 * - Multi-language support via QuestionCard
 */
const QuestionLibraryManager = ({ entityId, requestedSurveyType }) => {
    const { t } = useTranslation();
    const { background, text, cn } = useTheme();
    const { entitySettings } = usePreferences(); // Get entity settings for multi-language support

    // Multi-language settings from entity preferences
    const multiLanguageEnabled = entitySettings?.multi_language_enabled || false;
    const entityDefaultLanguage = entitySettings?.entity_default_language || 'en';

    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [surveyType, setSurveyType] = useState(
        requestedSurveyType === 'system' ? 'system' : 'service'
    ); // 'service' or 'system'
    const [questions, setQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all', 'scale', 'text', 'rating', 'yes_no'
    const [hasChanges, setHasChanges] = useState(false);
    const [permissions, setPermissions] = useState({
        canManageService: false,
        canManageSystem: false
    });

    // Load permissions and questions on mount
    useEffect(() => {
        loadPermissions();
    }, []);

    useEffect(() => {
        if (permissions.canManageService || permissions.canManageSystem) {
            loadQuestions();
        }
    }, [surveyType, permissions]);

    // Filter questions based on search and type filter
    useEffect(() => {
        let filtered = [...questions];

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(q =>
                q.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (q.help_text && q.help_text.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Apply type filter
        if (filterType !== 'all') {
            filtered = filtered.filter(q => q.question_type === filterType);
        }

        setFilteredQuestions(filtered);
    }, [questions, searchTerm, filterType]);

    /**
     * Load user permissions for managing survey types
     */
    const loadPermissions = async () => {
        try {
            const response = await axios.get(`/api/surveys/${entityId}/permissions`);
            setPermissions({
                canManageService: response.data.permissions?.service?.canManage || false,
                canManageSystem: response.data.permissions?.system?.canManage || false
            });

            const canManageService = response.data.permissions?.service?.canManage || false;
            const canManageSystem = response.data.permissions?.system?.canManage || false;

            // A parent request is a one-time initial selection. It is honored
            // only when permissions allow it, otherwise the legacy
            // permission-based default remains in force.
            if (requestedSurveyType === 'system' && canManageSystem) {
                setSurveyType('system');
            } else if (requestedSurveyType === 'service' && canManageService) {
                setSurveyType('service');
            } else if (canManageService) {
                setSurveyType('service');
            } else if (canManageSystem) {
                setSurveyType('system');
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
            toast.error(t('errorLoadingPermissions'));
        }
    };

    /**
     * Load global questions for selected survey type
     */
    const loadQuestions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `/api/surveys/library/${entityId}/${surveyType}?isGlobal=true`
            );
            setQuestions(response.data.data || []);
            setHasChanges(false);
        } catch (error) {
            console.error('Error loading questions:', error);
            toast.error(t('errorLoadingQuestions'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Add new global question
     */
    const handleAddQuestion = () => {
        const newQuestion = {
            question_type: 'scale',
            question_text: '',
            help_text: '',
            is_required: true,
            display_order: questions.length + 1,
            source_type: 'global',
            survey_type: surveyType
        };
        setQuestions([...questions, newQuestion]);
        setHasChanges(true);
    };

    /**
     * Handle question field change
     */
    const handleQuestionChange = (index, field, value) => {
        const updated = [...questions];
        updated[index] = {
            ...updated[index],
            [field]: value
        };
        setQuestions(updated);
        setHasChanges(true);
    };

    /**
     * Move question up/down
     */
    const handleMoveQuestion = (index, direction) => {
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === questions.length - 1)
        ) {
            return;
        }

        const updated = [...questions];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap questions
        [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];

        // Update display_order
        updated.forEach((q, i) => {
            q.display_order = i + 1;
        });

        setQuestions(updated);
        setHasChanges(true);
    };

    /**
     * Delete question
     */
    const handleDeleteQuestion = async (index) => {
        const question = questions[index];

        // If question has ID, delete from backend
        if (question.id) {
            try {
                await axios.delete(
                    `/api/surveys/library/${entityId}/${surveyType}/questions/${question.id}?isGlobal=true`
                );
                toast.success(t('questionDeleted'));
            } catch (error) {
                console.error('Error deleting question:', error);
                toast.error(error.response?.data?.error || t('errorDeletingQuestion'));
                return;
            }
        }

        // Remove from local state
        const updated = questions.filter((_, i) => i !== index);
        updated.forEach((q, i) => {
            q.display_order = i + 1;
        });
        setQuestions(updated);
        setHasChanges(question.id ? false : true);
    };

    /**
     * Save all changes
     */
    const handleSave = async () => {
        try {
            setSaving(true);

            // Validate questions
            for (const question of questions) {
                if (!question.question_text) {
                    toast.error(t('questionTextRequired'));
                    return;
                }
            }

            // Save each question
            for (const question of questions) {
                if (question.id) {
                    // Update existing question
                    await axios.put(
                        `/api/surveys/library/${entityId}/${surveyType}/questions/${question.id}`,
                        {
                            ...question,
                            isGlobal: true
                        }
                    );
                } else {
                    // Create new question
                    await axios.post(
                        `/api/surveys/library/${entityId}/${surveyType}`,
                        {
                            ...question,
                            isGlobal: true
                        }
                    );
                }
            }

            toast.success(t('questionsSaved'));
            setHasChanges(false);
            loadQuestions(); // Reload to get IDs for new questions
        } catch (error) {
            console.error('Error saving questions:', error);
            toast.error(error.response?.data?.error || t('errorSavingQuestions'));
        } finally {
            setSaving(false);
        }
    };

    // No permissions
    if (!permissions.canManageService && !permissions.canManageSystem) {
        return (
            <ThemedCard className="p-8">
                <div className={cn("text-center", text.secondary)}>
                    <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h2 className={cn("text-xl font-semibold mb-2", text.primary)}>
                        {t('noQuestionLibraryAccess')}
                    </h2>
                    <p>{t('contactAdministratorForLibraryAccess')}</p>
                </div>
            </ThemedCard>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-3">
                        <Library className={cn("w-8 h-8", text.primary)} />
                        <h1 className={cn("text-2xl font-bold", text.primary)}>
                            {t('globalQuestionLibrary')}
                        </h1>
                    </div>
                    <p className={cn("text-sm mt-1", text.secondary)}>
                        {t('manageReusableQuestions')}
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {hasChanges && (
                        <ThemedBadge variant="warning">
                            {t('unsavedChanges')}
                        </ThemedBadge>
                    )}
                    <ThemedButton
                        onClick={loadQuestions}
                        variant="outline"
                        disabled={loading || saving}
                    >
                        <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                        {t('refresh')}
                    </ThemedButton>
                    <ThemedButton
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? t('saving') : t('saveChanges')}
                    </ThemedButton>
                </div>
            </div>

            {/* Survey Type Selector */}
            <ThemedCard className="p-4">
                <div className="flex items-center gap-4">
                    <label className={cn("font-medium", text.primary)}>
                        {t('surveyType')}:
                    </label>
                    <div className="flex gap-2">
                        {permissions.canManageService && (
                            <button
                                onClick={() => setSurveyType('service')}
                                className={cn(
                                    "flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all",
                                    surveyType === 'service'
                                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                        : cn("border-transparent", background.tertiary)
                                )}
                            >
                                <Car className="w-4 h-4" />
                                <span className={cn("font-medium", text.primary)}>
                                    {t('serviceSurvey')}
                                </span>
                            </button>
                        )}
                        {permissions.canManageSystem && (
                            <button
                                onClick={() => setSurveyType('system')}
                                className={cn(
                                    "flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all",
                                    surveyType === 'system'
                                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                        : cn("border-transparent", background.tertiary)
                                )}
                            >
                                <Monitor className="w-4 h-4" />
                                <span className={cn("font-medium", text.primary)}>
                                    {t('systemSurvey')}
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            </ThemedCard>

            {/* Filters and Search */}
            <ThemedCard className="p-4">
                <div className="flex items-center gap-4">
                    {/* Search */}
                    <div className="flex-1">
                        <div className="relative">
                            <Search className={cn("absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4", text.secondary)} />
                            <ThemedInput
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t('searchQuestions')}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    {/* Type filter */}
                    <div className="w-48">
                        <ThemedSelect
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="all">{t('allTypes')}</option>
                            <option value="scale_1_5">{t('ratingScale')}</option>
                            <option value="rating">{t('starRating')}</option>
                            <option value="text">{t('textResponse')}</option>
                            <option value="yes_no">{t('yesNo')}</option>
                        </ThemedSelect>
                    </div>

                    {/* Add question button */}
                    <ThemedButton onClick={handleAddQuestion}>
                        <Plus className="w-4 h-4 mr-2" />
                        {t('addQuestion')}
                    </ThemedButton>
                </div>
            </ThemedCard>

            {/* Questions List */}
            {loading ? (
                <div className={cn("flex justify-center items-center h-64", text.secondary)}>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3">{t('loading')}...</span>
                </div>
            ) : filteredQuestions.length === 0 ? (
                <ThemedCard className="p-8">
                    <div className={cn("text-center", text.secondary)}>
                        <Library className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <h3 className={cn("text-lg font-semibold mb-2", text.primary)}>
                            {searchTerm || filterType !== 'all'
                                ? t('noMatchingQuestions')
                                : t('noQuestionsYet')
                            }
                        </h3>
                        <p className="mb-4">
                            {searchTerm || filterType !== 'all'
                                ? t('tryDifferentSearchOrFilter')
                                : t('addYourFirstGlobalQuestion')
                            }
                        </p>
                        {!searchTerm && filterType === 'all' && (
                            <ThemedButton onClick={handleAddQuestion}>
                                <Plus className="w-4 h-4 mr-2" />
                                {t('addQuestion')}
                            </ThemedButton>
                        )}
                    </div>
                </ThemedCard>
            ) : (
                <div className="space-y-4">
                    {filteredQuestions.map((question, index) => (
                        <QuestionCard
                            key={question.id || index}
                            question={question}
                            index={index}
                            totalQuestions={filteredQuestions.length}
                            entityId={null} // Global questions have entityId = null
                            isGlobal={true}
                            canEdit={true}
                            canDelete={true}
                            canReorder={!searchTerm && filterType === 'all'} // Only allow reorder when not filtered
                            multiLanguageEnabled={multiLanguageEnabled}
                            entityDefaultLanguage={entityDefaultLanguage}
                            onChange={handleQuestionChange}
                            onDelete={handleDeleteQuestion}
                            onMove={handleMoveQuestion}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default QuestionLibraryManager;
